/* OCC stage 2: native Shopify results, isolated keyboard/request state. */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.occConnected) return;
    this.occConnected = true;
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('[data-predictive-search]');
    this.status = this.querySelector('.predictive-search-status');
    if (!this.input || !this.panel) return;
    this.panel.id = this.input.id + '-suggestions';
    this.panel.setAttribute('role', 'listbox');
    this.panel.setAttribute('aria-label', 'Search suggestions');
    this.input.setAttribute('aria-controls', this.panel.id);
    this.input.removeAttribute('aria-owns');
    this.status?.removeAttribute('aria-hidden');
    this.events = new AbortController();
    const listen = (el, type, fn) => el.addEventListener(type, fn, {signal: this.events.signal});
    listen(this.input, 'input', () => {
      this.controller?.abort(); clearTimeout(this.timer); this.close(); this.toggleReset();
      this.timer = setTimeout(() => this.search(), 220);
    });
    listen(this.input, 'focus', () => { if (this.input.value.trim()) this.search(); });
    listen(this.input, 'keydown', event => this.onKey(event));
    listen(this.input.form, 'submit', event => {
      if (!this.input.value.trim()) { event.preventDefault(); this.input.focus(); }
    });
    listen(this.input.form, 'reset', event => {
      event.preventDefault(); this.input.value = ''; this.close();
      this.controller?.abort(); clearTimeout(this.timer); this.toggleReset(); this.input.focus();
    });
    listen(this, 'focusout', () => setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 0));
  }
  disconnectedCallback() {
    this.events?.abort(); this.controller?.abort(); clearTimeout(this.timer); this.occConnected = false;
  }
  toggleReset() { this.querySelector('[type="reset"]')?.classList.toggle('hidden', !this.input.value); }
  close() {
    this.removeAttribute('open'); this.removeAttribute('loading');
    this.input.setAttribute('aria-expanded', 'false'); this.input.removeAttribute('aria-activedescendant');
    this.querySelectorAll('[aria-selected="true"]').forEach(x => x.setAttribute('aria-selected', 'false'));
  }
  announce(text) { if (this.status) this.status.textContent = text; }
  async search() {
    const query = this.input.value.trim();
    if (!query) { this.close(); return; }
    this.controller?.abort();
    const controller = this.controller = new AbortController();
    let timedOut = false;
    const deadline = setTimeout(() => { timedOut = true; controller.abort(); }, 8000);
    this.setAttribute('loading', 'true'); this.announce('Loading search suggestions.');
    try {
      const url = new URL(routes.predictive_search_url, location.origin);
      url.search = new URLSearchParams({q: query, section_id: 'predictive-search',
        'resources[type]': 'product,collection,query', 'resources[limit]': '5', 'resources[limit_scope]': 'each'});
      const response = await fetch(url, {signal: controller.signal});
      if (!response.ok) throw new Error('Search response ' + response.status);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = doc.querySelector('#shopify-section-predictive-search');
      if (!source) throw new Error('Search section missing');
      if (controller.signal.aborted || query !== this.input.value.trim()) return;
      this.panel.innerHTML = source.innerHTML;
      this.panel.querySelectorAll('[role="option"]').forEach((item, index) => { item.id = this.panel.id + '-option-' + index; });
      this.setAttribute('results', 'true'); this.setAttribute('open', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      this.panel.style.maxHeight = Math.max(180, window.innerHeight - this.input.getBoundingClientRect().bottom - 28) + 'px';
      this.announce(this.panel.querySelector('[data-occ-count]')?.textContent || 'Search suggestions available.');
    } catch (error) {
      if (this.controller !== controller || (controller.signal.aborted && !timedOut)) return;
      this.panel.replaceChildren();
      const message = document.createElement('p');
      message.className = 'occ-search-recovery';
      message.textContent = 'Suggestions are unavailable. Press Enter or use the search button to see results.';
      this.panel.append(message); this.setAttribute('open', 'true'); this.input.setAttribute('aria-expanded', 'true');
      this.announce(message.textContent);
    } finally { clearTimeout(deadline); if (this.controller === controller) this.removeAttribute('loading'); }
  }
  onKey(event) {
    if (event.isComposing) return;
    if (event.key === 'Escape') {
      if (this.hasAttribute('open')) { event.preventDefault(); event.stopPropagation(); this.controller?.abort(); clearTimeout(this.timer); this.close(); }
      return;
    }
    if (!this.hasAttribute('open')) return;
    const options = [...this.panel.querySelectorAll('[role="option"]')].filter(x => x.getClientRects().length);
    const selected = options.findIndex(x => x.getAttribute('aria-selected') === 'true');
    if (event.key === 'Enter' && selected >= 0) {
      event.preventDefault(); options[selected].querySelector('a,button')?.click(); return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !options.length) return;
    event.preventDefault();
    const next = selected < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1)
      : (selected + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
    options.forEach((x, i) => x.setAttribute('aria-selected', String(i === next)));
    this.input.setAttribute('aria-activedescendant', options[next].id);
    options[next].scrollIntoView({block: 'nearest'});
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
